import {
  Check,
  FileSpreadsheet,
  FlaskConical,
  Info,
  LoaderCircle,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  X
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiClient,
  type UploadTemplateAdmin,
  type UploadTemplateCheck,
  type UploadTemplateColumn,
  type UploadTemplateInput,
  type UploadTemplateVocabulary
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { authKindsFor } from "@/shared/vocabulary/scraper-auth-kinds";
import { ScraperCredentialSection } from "./ScraperCredentialSection";
import {
  canonicalFieldLabel,
  groupCanonicalFields,
  isImportableDimension
} from "@/shared/vocabulary/canonical-fields";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { Select } from "@/shared/components/Select";

/**
 * Administração → Templates de upload.
 *
 * Até 2026-09-22 os 7 templates de clipadora viviam num JSON do `core/`,
 * e uma coluna renomeada pela clipadora era deploy. Aqui o Máster
 * cadastra, edita, desativa e reativa; a tela de envio lê o mesmo
 * cadastro (só os ativos) no próximo envio.
 *
 * O formulário é a definição INTEIRA: nome, fonte, scraper e a grade de
 * colunas — uma linha por coluna da planilha, com o campo que ela
 * alimenta, se é obrigatória (as obrigatórias são o que RECONHECE o
 * template ao soltar a planilha), prioridade, padrão de extração e
 * separador de junção. "Testar com uma planilha" manda o rascunho mais
 * um arquivo de verdade e mostra o que falta e qual template ativo
 * casaria com o arquivo hoje; "Trazer colunas do arquivo" preenche a
 * grade com o cabeçalho, que é como um template novo começa.
 *
 * Editar NÃO reprocessa nada: vale pro próximo envio.
 */

const SOURCE_LABELS: Record<string, string> = {
  press: "Imprensa",
  network: "Rede"
};

export const fieldLabel = canonicalFieldLabel;

type Draft = {
  name: string;
  post_source: string;
  scraper: string;
  columns: UploadTemplateColumn[];
};

const EMPTY_COLUMN: UploadTemplateColumn = {
  raw_column_name: "",
  canonical_field: null,
  priority: 0,
  extract_pattern: null,
  join_with: null,
  is_required: false
};

const EMPTY_DRAFT: Draft = { name: "", post_source: "press", scraper: "", columns: [] };

function draftOf(template: UploadTemplateAdmin): Draft {
  return {
    name: template.name,
    post_source: template.post_source,
    scraper: template.scraper ?? "",
    columns: template.columns.map(column => ({ ...column }))
  };
}

export function payloadOf(draft: Draft): UploadTemplateInput {
  return {
    name: draft.name.trim(),
    post_source: draft.post_source,
    scraper: draft.scraper || null,
    columns: draft.columns.map(column => ({
      raw_column_name: column.raw_column_name.trim(),
      canonical_field: column.canonical_field || null,
      priority: Number.isFinite(column.priority) ? column.priority : 0,
      extract_pattern: column.extract_pattern?.trim() || null,
      join_with: column.join_with || null,
      is_required: column.is_required
    }))
  };
}

/** Preenche a grade com o cabeçalho de uma planilha: mantém o que já
 *  está mapeado e acrescenta as colunas que faltam, sem campo. */
export function mergeFileColumns(
  columns: UploadTemplateColumn[],
  fileColumns: string[]
): UploadTemplateColumn[] {
  const known = new Set(columns.map(column => column.raw_column_name.trim()));
  const added = fileColumns
    .filter(name => !known.has(name))
    .map(name => ({ ...EMPTY_COLUMN, raw_column_name: name }));
  return [...columns, ...added];
}

export function UploadTemplatesAdmin({ token }: { token: string }) {
  const [templates, setTemplates] = useState<UploadTemplateAdmin[]>([]);
  // A lista só pode dizer "nenhum template" depois de ter respondido: sem
  // isto, a falha da carga inicial caía no vazio e afirmava um cadastro
  // vazio (2026-10-09, teste de render).
  const [listed, setListed] = useState(false);
  const [vocabulary, setVocabulary] = useState<UploadTemplateVocabulary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `null` = lista; `"new"` = criando; número = editando aquele id.
  const [editing, setEditing] = useState<"new" | number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    // `allSettled`, não `all`: com `all`, a falha do vocabulário jogava
    // fora a lista que tinha chegado, e "Novo template" levava a um beco
    // (o botão do cabeçalho sumia e o editor não montava sem vocabulário).
    // Cada metade que chega vai pra tela; a que falha vira erro com
    // "Tentar de novo" (2026-10-09, teste de render).
    const [rows, vocab] = await Promise.allSettled([
      apiClient.listUploadTemplatesAdmin(token),
      apiClient.uploadTemplateVocabulary(token)
    ]);
    if (rows.status === "fulfilled") {
      setTemplates(rows.value);
      setListed(true);
    }
    if (vocab.status === "fulfilled") setVocabulary(vocab.value);
    const failure =
      rows.status === "rejected" ? rows.reason : vocab.status === "rejected" ? vocab.reason : null;
    setError(
      failure === null ? null : errorMessage(failure, "Não foi possível carregar os templates.")
    );
    setLoading(false);
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (template: UploadTemplateAdmin) => {
    setBusy(template.id);
    setError(null);
    try {
      if (template.is_active) await apiClient.deactivateUploadTemplate(token, template.id);
      else await apiClient.reactivateUploadTemplate(token, template.id);
      await load();
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível alterar o template."));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <ApiState kind="loading">Carregando templates…</ApiState>;

  // Sem o vocabulário o editor não monta: o botão fica à vista, apagado,
  // e o erro abaixo traz o "Tentar de novo".
  const cannotEdit = vocabulary === null;
  const current = typeof editing === "number" ? templates.find(t => t.id === editing) : undefined;

  return (
    <div className="copiloto-admin ajustes-panel admin-panel">
      <AdminPageHeader
        title="Templates de upload"
        description="Como cada clipadora entrega a planilha: quais colunas existem, o que cada uma alimenta e quais reconhecem o template ao soltar o arquivo."
        actions={
          editing === null && (
            <button
              className="primary-button"
              type="button"
              disabled={cannotEdit}
              title={cannotEdit ? "O vocabulário de campos não carregou." : undefined}
              onClick={() => setEditing("new")}
            >
              <Plus size={16} /> Novo template
            </button>
          )
        }
      />

      <p className="cena-notice">
        <Info size={14} aria-hidden="true" /> Editar um template vale para os{" "}
        <strong>próximos envios</strong>; nada do que já entrou é reprocessado. Desativar tira
        o template da escolha, e os uploads antigos dele continuam íntegros.
      </p>

      {error && (cannotEdit || !listed) ? (
        <ApiState kind="error" onRetry={() => void load()}>
          <span role="alert">{error}</span>
        </ApiState>
      ) : (
        error && <p className="form-error" role="alert">{error}</p>
      )}

      {editing !== null && vocabulary ? (
        <TemplateEditor
          token={token}
          vocabulary={vocabulary}
          template={current ?? null}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : (
        <div className="card">
          <div className="card-title">
            <div>
              <FileSpreadsheet size={18} aria-hidden="true" />
              <h3>Cadastrados</h3>
              <p>Template desativado some do select de envio, mas continua no histórico.</p>
            </div>
          </div>
          {!listed ? null : templates.length === 0 ? (
            <p className="copiloto-empty-list">Nenhum template cadastrado.</p>
          ) : (
            <table className="ai-admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Fonte</th>
                  <th>Scraper</th>
                  <th>Colunas</th>
                  <th>Situação</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {templates.map(template => {
                  const required = template.columns.filter(c => c.is_required).length;
                  return (
                    <tr key={template.id} className={template.is_active ? undefined : "row-inactive"}>
                      <td><strong>{template.name}</strong></td>
                      <td>{SOURCE_LABELS[template.post_source] ?? template.post_source}</td>
                      <td>{template.scraper ?? "—"}</td>
                      <td>
                        {template.columns.length} ({required} obrigat{required === 1 ? "ória" : "órias"})
                      </td>
                      <td>
                        <span className={`badge-pill ${template.is_active ? "success" : "inactive"}`}>
                          {template.is_active ? "Ativo" : "Desativado"}
                        </span>
                      </td>
                      <td className="ai-admin-row-actions">
                        <button
                          className="text-button"
                          type="button"
                          disabled={cannotEdit}
                          onClick={() => setEditing(template.id)}
                        >
                          <Pencil size={14} /> Editar
                        </button>{" "}
                        <button
                          className="text-button"
                          type="button"
                          disabled={busy === template.id}
                          onClick={() => void toggle(template)}
                        >
                          {busy === template.id ? (
                            <LoaderCircle className="spin" size={14} />
                          ) : template.is_active ? (
                            <Trash2 size={14} />
                          ) : (
                            <RotateCcw size={14} />
                          )}{" "}
                          {template.is_active ? "Desativar" : "Reativar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({
  token,
  vocabulary,
  template,
  onCancel,
  onSaved
}: {
  token: string;
  vocabulary: UploadTemplateVocabulary;
  template: UploadTemplateAdmin | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<Draft>(template ? draftOf(template) : EMPTY_DRAFT);
  const mapsClassification = draft.columns.some(
    column => column.canonical_field !== null && isImportableDimension(column.canonical_field)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<UploadTemplateCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const setColumn = (index: number, patch: Partial<UploadTemplateColumn>) =>
    setDraft(current => ({
      ...current,
      columns: current.columns.map((column, i) => (i === index ? { ...column, ...patch } : column))
    }));

  const removeColumn = (index: number) =>
    setDraft(current => ({ ...current, columns: current.columns.filter((_, i) => i !== index) }));

  const addColumn = () =>
    setDraft(current => ({ ...current, columns: [...current.columns, { ...EMPTY_COLUMN }] }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = payloadOf(draft);
      if (template) await apiClient.updateUploadTemplate(token, template.id, payload);
      else await apiClient.createUploadTemplate(token, payload);
      await onSaved();
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível salvar o template."));
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async (file: File) => {
    setChecking(true);
    setError(null);
    try {
      setCheck(await apiClient.checkUploadTemplate(token, payloadOf(draft), file));
    } catch (failure) {
      setCheck(null);
      setError(errorMessage(failure, "Não foi possível ler a planilha."));
    } finally {
      setChecking(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const isPress = draft.post_source === "press";
  const requiredCount = draft.columns.filter(column => column.is_required).length;
  // As três opções de exceção (prioridade, extração, junção) ficam
  // recolhidas: a maioria das colunas só precisa de nome, campo e
  // "obrigatória". Abrem sozinhas quando o template já usa alguma —
  // esconder um valor gravado seria pior que mostrar a coluna a mais.
  const usesAdvanced = draft.columns.some(
    column => column.priority !== 0 || Boolean(column.extract_pattern) || Boolean(column.join_with)
  );
  const [showAdvanced, setShowAdvanced] = useState(usesAdvanced);
  const advanced = showAdvanced || usesAdvanced;

  return (
    <div className="ai-admin-stack">
      <div className="card">
        <div className="card-title">
          <div>
            {template ? <Pencil size={18} aria-hidden="true" /> : <Plus size={18} aria-hidden="true" />}
            <h3>{template ? `Editar ${template.name}` : "Novo template"}</h3>
            <p>Quem é a clipadora e de onde vem o texto das matérias.</p>
          </div>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="admin-form-grid">
          <div className="admin-field-group">
            <label className="field">
              <span>Nome</span>
              <input
                value={draft.name}
                placeholder="wclipper"
                maxLength={60}
                aria-describedby="template-name-help"
                onChange={event => setDraft({ ...draft, name: event.target.value })}
              />
            </label>
            <small id="template-name-help" className="admin-field-help">
              É o que aparece na escolha de template do envio. Renomear não afeta os uploads
              já feitos.
            </small>
          </div>
          <div className="admin-field-group">
            <label className="field">
              <span>Fonte</span>
              <Select
                value={draft.post_source}
                aria-describedby="template-source-help"
                onChange={event =>
                  setDraft({
                    ...draft,
                    post_source: event.target.value,
                    scraper: event.target.value === "press" ? draft.scraper : ""
                  })
                }
              >
                {vocabulary.post_sources.map(source => (
                  <option key={source} value={source}>
                    {SOURCE_LABELS[source] ?? source}
                  </option>
                ))}
              </Select>
            </label>
            <small id="template-source-help" className="admin-field-help">
              Imprensa são matérias de veículos; rede são posts de redes sociais.
            </small>
          </div>
          <div className="admin-field-group">
            <label className="field">
              <span>Scraper de conteúdo</span>
              <Select
                value={draft.scraper}
                disabled={!isPress}
                aria-describedby="template-scraper-help"
                onChange={event => setDraft({ ...draft, scraper: event.target.value })}
              >
                <option value="">Nenhum (o texto vem na planilha)</option>
                {vocabulary.scrapers.map(scraper => (
                  <option key={scraper} value={scraper}>
                    {scraper}
                  </option>
                ))}
              </Select>
            </label>
            <small id="template-scraper-help" className="admin-field-help">
              {isPress
                ? "Busca o texto da matéria no site da clipadora quando a planilha só traz o link."
                : "Post de rede já chega com o texto na planilha."}
            </small>
          </div>
        </div>
      </div>

      {template && authKindsFor(draft.scraper).length > 0 && (
        draft.scraper === template.scraper ? (
          <ScraperCredentialSection
            token={token}
            templateId={template.id}
            scraper={draft.scraper}
          />
        ) : (
          <p className="admin-field-help">
            Salve o template com este scraper antes de cadastrar a credencial.
          </p>
        )
      )}

      <div className="card">
        <div className="card-title">
          <div>
            <FileSpreadsheet size={18} aria-hidden="true" />
            <h3>Colunas</h3>
            <p>
              Uma linha por coluna da planilha, com o nome exatamente como vem no
              cabeçalho. As obrigatórias são as que fazem o arquivo ser reconhecido como
              deste template.
            </p>
          </div>
        </div>

        <div className="template-toolbar">
          <span className="template-toolbar-count">
            <strong>{draft.columns.length}</strong>{" "}
            {draft.columns.length === 1 ? "coluna" : "colunas"} ·{" "}
            <strong>{requiredCount}</strong>{" "}
            {requiredCount === 1 ? "obrigatória" : "obrigatórias"}
          </span>
          <label
            className="admin-switch"
            title={usesAdvanced ? "Em uso neste template — fica à vista" : undefined}
          >
            <input
              type="checkbox"
              checked={advanced}
              disabled={usesAdvanced}
              onChange={event => setShowAdvanced(event.target.checked)}
            />
            <span>Opções avançadas</span>
          </label>
        </div>

        {draft.columns.length === 0 ? (
          <div className="admin-empty">
            <FileSpreadsheet size={22} aria-hidden="true" />
            <p>Nenhuma coluna ainda — traga o cabeçalho de uma planilha ou adicione à mão.</p>
          </div>
        ) : (
          <div className="admin-table-scroll">
            <table className="ai-admin-table template-columns">
              <thead>
                <tr>
                  <th className="template-col-index" aria-label="Número" />
                  <th>Coluna na planilha</th>
                  <th>Alimenta o campo</th>
                  <th className="template-col-check">Obrigatória</th>
                  {advanced && (
                    <>
                      <th>Prioridade</th>
                      <th>Padrão de extração</th>
                      <th>Junção</th>
                    </>
                  )}
                  <th aria-label="Remover" />
                </tr>
              </thead>
              <tbody>
                {draft.columns.map((column, index) => (
                  <tr key={index}>
                    <td className="template-col-index">{index + 1}</td>
                    <td>
                      <input
                        aria-label={`Coluna ${index + 1}`}
                        placeholder="Nome no cabeçalho"
                        value={column.raw_column_name}
                        onChange={event => setColumn(index, { raw_column_name: event.target.value })}
                      />
                    </td>
                    <td>
                      <Select
                        aria-label={`Campo da coluna ${index + 1}`}
                        value={column.canonical_field ?? ""}
                        className={column.canonical_field ? undefined : "is-empty"}
                        onChange={event =>
                          setColumn(index, { canonical_field: event.target.value || null })
                        }
                      >
                        <option value="">Nenhum (só reconhece o template)</option>
                        {groupCanonicalFields(vocabulary.canonical_fields).map(group => (
                          <optgroup key={group.group} label={group.label}>
                            {group.fields.map(field => (
                              <option key={field} value={field}>
                                {fieldLabel(field)}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Select>
                    </td>
                    <td className="template-col-check">
                      <input
                        type="checkbox"
                        aria-label={`Coluna ${index + 1} obrigatória`}
                        checked={column.is_required}
                        onChange={event => setColumn(index, { is_required: event.target.checked })}
                      />
                    </td>
                    {advanced && (
                      <>
                        <td>
                          <input
                            type="number"
                            min={0}
                            className="template-priority"
                            aria-label={`Prioridade da coluna ${index + 1}`}
                            value={column.priority}
                            onChange={event =>
                              setColumn(index, {
                                priority: Number.parseInt(event.target.value, 10) || 0
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="template-pattern"
                            aria-label={`Padrão de extração da coluna ${index + 1}`}
                            placeholder="opcional"
                            value={column.extract_pattern ?? ""}
                            onChange={event =>
                              setColumn(index, { extract_pattern: event.target.value || null })
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="template-join"
                            aria-label={`Separador de junção da coluna ${index + 1}`}
                            placeholder="—"
                            value={column.join_with ?? ""}
                            onChange={event =>
                              setColumn(index, { join_with: event.target.value || null })
                            }
                          />
                        </td>
                      </>
                    )}
                    <td className="ai-admin-row-actions">
                      <button
                        className="icon-button"
                        type="button"
                        aria-label={`Remover coluna ${index + 1}`}
                        title="Remover coluna"
                        onClick={() => removeColumn(index)}
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {advanced && (
          <dl className="template-legend">
            <div>
              <dt>Prioridade</dt>
              <dd>
                Quando duas colunas alimentam o mesmo campo, a de número menor é lida
                primeiro — a primeira com valor vence.
              </dd>
            </div>
            <div>
              <dt>Padrão de extração</dt>
              <dd>
                Expressão regular aplicada ao valor; o primeiro grupo entre parênteses vira o
                valor do campo. Ex.: <code>n=(\d+)</code> tira o id de dentro de um link.
              </dd>
            </div>
            <div>
              <dt>Junção</dt>
              <dd>
                Em vez de escolher uma, junta as colunas do mesmo campo com este separador —
                como Data e Hora em colunas separadas virando uma data só.
              </dd>
            </div>
          </dl>
        )}

        {mapsClassification && (
          <p className="template-classification-note" role="note">
            Colunas de <strong>classificação pronta</strong> não ligam nada sozinhas: quem decide
            usá-las é a pessoa, no envio, em “Configurações avançadas”. Subtemas e mensagens-chave
            vão na mesma célula, separados por ponto e vírgula (;). O texto tem que vir como o
            Reputaition escreve — não há tabela de sinônimos; o que não casar vai para a fila
            de correção.
          </p>
        )}

        <div className="template-editor-actions">
          <button className="secondary-button" type="button" onClick={addColumn}>
            <Plus size={15} aria-hidden="true" /> Adicionar coluna
          </button>
          <label className={`secondary-button template-file-button${checking ? " is-busy" : ""}`}>
            {checking ? (
              <LoaderCircle className="spin" size={15} aria-hidden="true" />
            ) : (
              <FlaskConical size={15} aria-hidden="true" />
            )}
            Testar com uma planilha
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              disabled={checking}
              onChange={event => {
                const file = event.target.files?.[0];
                if (file) void runCheck(file);
              }}
            />
          </label>
          <span className="admin-field-help">
            Confere o rascunho contra um arquivo de verdade, sem gravar nada.
          </span>
        </div>

        {check && (
          <div
            className={`template-check ${check.missing_columns.length > 0 ? "has-missing" : "is-ok"}`}
            role="status"
          >
            <p className="template-check-head">
              {check.missing_columns.length > 0 ? (
                <X size={15} aria-hidden="true" />
              ) : (
                <Check size={15} aria-hidden="true" />
              )}
              <span>
                <strong>{check.columns.length}</strong> colunas e{" "}
                <strong>{check.row_count.toLocaleString("pt-BR")}</strong> linhas na planilha.{" "}
                {check.detected_by
                  ? <>Hoje ela seria reconhecida como <strong>{check.detected_by}</strong>.</>
                  : "Hoje nenhum template ativo a reconhece."}
              </span>
            </p>
            {check.missing_columns.length > 0 ? (
              <p className="template-check-missing">
                Obrigatórias que a planilha não tem: {check.missing_columns.join(", ")}.
              </p>
            ) : (
              <p className="template-check-ok">Todas as obrigatórias existem na planilha.</p>
            )}
            <button
              className="text-button"
              type="button"
              onClick={() =>
                setDraft(current => ({
                  ...current,
                  columns: mergeFileColumns(current.columns, check.columns)
                }))
              }
            >
              <Plus size={14} /> Trazer as colunas do arquivo para a grade
            </button>
          </div>
        )}
      </div>

      <div className="admin-savebar">
        <span className="admin-savebar-status">
          Vale para os próximos envios; nada do que já entrou é reprocessado.
        </span>
        <div className="admin-savebar-actions">
          <button className="secondary-button" type="button" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={saving || !draft.name.trim()}
            onClick={() => void save()}
          >
            {saving ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
            {template ? "Salvar alterações" : "Cadastrar template"}
          </button>
        </div>
      </div>
    </div>
  );
}
